import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import PagePicker from '@/components/growth/PagePicker';
import {
  ShoppingBag,
  Users,
  Target,
  Zap,
  TrendingUp,
  Search,
  AlertTriangle,
  ArrowRight,
  Layers,
  Activity,
  Package,
  UserCheck,
  MousePointer,
  BarChart3,
  FileText
} from 'lucide-react';

interface ThreadCard {
  type: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  count?: number;
  trend?: 'up' | 'down' | 'neutral';
}

interface QuickWin {
  type: string;
  title: string;
  effort: 'minimal' | 'moderate' | 'significant';
  impact: 'low' | 'medium' | 'high';
  issue?: string;
}

interface FrictionAlert {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  friction_type: string;
  affected_entity_name: string;
  event_count: number;
  recommended_action: string;
  created_at: string;
}

export default function GrowthDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(false);
  const [quickWins, setQuickWins] = useState<QuickWin[]>([]);
  const [frictionAlerts, setFrictionAlerts] = useState<FrictionAlert[]>([]);
  const [viewMode, setViewMode] = useState<'agency' | 'site-owner'>('agency');
  const [entityStats, setEntityStats] = useState<any>(null);
  const [selectedPage, setSelectedPage] = useState<any>(null);
  const [pageAnalysis, setPageAnalysis] = useState<any>(null);

  // Determine active tab based on URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/growth/competitors')) return 'analysis';
    if (path.includes('/growth/content-gaps')) return 'analysis';
    if (path.includes('/growth/journey')) return 'analysis';
    if (path.includes('/growth/metrics')) return 'analysis';
    if (path.includes('/growth/analyze')) return 'analysis';
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());

  useEffect(() => {
    if (selectedSite?._id) {
      fetchDashboardData();
    }
  }, [selectedSite]);

  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  const fetchDashboardData = async () => {
    if (!selectedSite?._id) return;
    
    setLoading(true);
    try {
      // Fetch quick wins
      const quickWinsRes = await authFetch(
        `${API_URL}/growth/quick-wins?siteId=${selectedSite._id}`
      );
      const quickWinsData = await quickWinsRes.json();
      setQuickWins(quickWinsData.quickWins || []);

      // Fetch friction alerts
      const alertsRes = await authFetch(
        `${API_URL}/growth/friction-alerts?siteId=${selectedSite._id}&status=active`
      );
      const alertsData = await alertsRes.json();
      setFrictionAlerts(alertsData.alerts || []);

      // Fetch entity statistics (placeholder - would need real endpoint)
      // This would show counts of products, categories, segments analyzed
      setEntityStats({
        categories: 12,
        products: 156,
        segments: 5,
        intents: 8
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleThreadClick = (type: string) => {
    // Navigate to thread analysis view
    navigate(`/growth/thread/${type}`);
  };

  const handleQuickWinClick = (quickWin: QuickWin) => {
    // Could open a modal or navigate to implementation view
    console.log('Implement quick win:', quickWin);
  };

  const acknowledgeAlert = async (alertId: string) => {
    try {
      await authFetch(
        `${API_URL}/growth/friction-alerts/${alertId}/acknowledge`,
        { method: 'POST' }
      );
      // Refresh alerts
      fetchDashboardData();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const handlePageSelect = async (page: any) => {
    setSelectedPage(page);
    setLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/growth/thread/page/${encodeURIComponent(page.url)}?siteId=${selectedSite?._id}`
      );
      const analysis = await response.json();
      setPageAnalysis(analysis);
    } catch (error) {
      console.error('Error analyzing page:', error);
    } finally {
      setLoading(false);
    }
  };

  const threadCards: ThreadCard[] = [
    {
      type: 'page',
      title: 'Individual Pages',
      description: 'Analyze specific page performance and issues',
      icon: <FileText className="w-5 h-5" />,
      count: entityStats?.pages,
      trend: 'neutral'
    },
    {
      type: 'category',
      title: 'Product Categories',
      description: 'Analyze performance and opportunities by category',
      icon: <Layers className="w-5 h-5" />,
      count: entityStats?.categories,
      trend: 'up'
    },
    {
      type: 'product',
      title: 'Individual Products',
      description: 'Deep dive into specific product performance',
      icon: <Package className="w-5 h-5" />,
      count: entityStats?.products,
      trend: 'neutral'
    },
    {
      type: 'segment',
      title: 'Customer Segments',
      description: 'Understand different customer groups',
      icon: <UserCheck className="w-5 h-5" />,
      count: entityStats?.segments,
      trend: 'up'
    },
    {
      type: 'intent',
      title: 'User Intent',
      description: 'Optimize for different user intentions',
      icon: <Target className="w-5 h-5" />,
      count: entityStats?.intents,
      trend: 'down'
    },
    {
      type: 'friction',
      title: 'Friction Points',
      description: 'Identify and fix user experience issues',
      icon: <AlertTriangle className="w-5 h-5" />,
      count: frictionAlerts.length,
      trend: frictionAlerts.length > 5 ? 'down' : 'neutral'
    },
    {
      type: 'performance',
      title: 'Page Performance',
      description: 'Analyze conversion and engagement metrics',
      icon: <Activity className="w-5 h-5" />,
      trend: 'up'
    }
  ];

  const getImpactBadgeColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  const getEffortBadgeColor = (effort: string) => {
    switch (effort) {
      case 'minimal': return 'bg-blue-500';
      case 'moderate': return 'bg-orange-500';
      case 'significant': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'warning': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'info': return 'bg-blue-100 border-blue-300 text-blue-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertDescription>
            Please select a site from the header to view growth insights.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold">Growth Intelligence</h1>
            <p className="text-muted-foreground mt-1">
              Data-driven insights to grow {selectedSite.domain}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'agency' ? 'default' : 'outline'}
              onClick={() => setViewMode('agency')}
              size="sm"
            >
              Agency View
            </Button>
            <Button
              variant={viewMode === 'site-owner' ? 'default' : 'outline'}
              onClick={() => setViewMode('site-owner')}
              size="sm"
            >
              Site Owner View
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pages">Pages</TabsTrigger>
          <TabsTrigger value="quick-wins">Quick Wins</TabsTrigger>
          <TabsTrigger value="alerts">Friction Alerts</TabsTrigger>
          <TabsTrigger value="analysis">Deep Analysis</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          {/* Friction Alerts Summary */}
          {frictionAlerts.length > 0 && (
            <Alert className="mb-6 border-orange-200 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                <span className="font-semibold">
                  {frictionAlerts.filter(a => a.severity === 'critical').length} critical issues
                </span>
                {' '}detected affecting user experience.
                {' '}
                <button
                  className="underline hover:no-underline"
                  onClick={() => navigate('/growth/alerts')}
                >
                  View all alerts →
                </button>
              </AlertDescription>
            </Alert>
          )}

          {/* Thread Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {threadCards.map((card) => (
              <Card
                key={card.type}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleThreadClick(card.type)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      {card.icon}
                    </div>
                    {card.count !== undefined && (
                      <Badge variant="secondary">{card.count}</Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg mt-3">{card.title}</CardTitle>
                  <CardDescription className="text-sm">
                    {card.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="ghost" className="w-full" size="sm">
                    Analyze
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Quick Wins Available
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{quickWins.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  High impact, low effort
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Active Friction Points
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{frictionAlerts.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Requiring attention
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Categories Analyzed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{entityStats?.categories || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  With gap analysis
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Conversion Impact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">+12%</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Potential improvement
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages">
          <PagePicker
            siteId={selectedSite?._id || ''}
            onPageSelect={handlePageSelect}
            selectedPage={selectedPage}
          />

          {/* Page Analysis Panel */}
          {pageAnalysis && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Page Analysis: {selectedPage?.title || selectedPage?.url}</CardTitle>
                <CardDescription>
                  Health Score: {pageAnalysis.summary?.overallHealth || 'Unknown'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold">
                        {pageAnalysis.summary?.frictionScore || 0}
                      </div>
                      <p className="text-sm text-muted-foreground">Friction Score</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold">
                        {pageAnalysis.summary?.performanceScore || 0}
                      </div>
                      <p className="text-sm text-muted-foreground">Performance Score</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold">
                        {pageAnalysis.summary?.contentScore || 0}
                      </div>
                      <p className="text-sm text-muted-foreground">Content Score</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Friction Issues */}
                {pageAnalysis.friction && pageAnalysis.friction.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Friction Points</h3>
                    <div className="space-y-2">
                      {pageAnalysis.friction.map((f: any, idx: number) => (
                        <Alert key={idx} className="border-orange-200 bg-orange-50">
                          <AlertTriangle className="h-4 w-4 text-orange-600" />
                          <AlertDescription>
                            <strong>{f.friction_type}:</strong> {f.occurrences} occurrences affecting {f.affected_sessions} sessions
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {pageAnalysis.recommendations && pageAnalysis.recommendations.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Recommendations</h3>
                    <div className="space-y-2">
                      {pageAnalysis.recommendations.map((rec: any, idx: number) => (
                        <Card key={idx}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium">{rec.title}</h4>
                                <p className="text-sm text-muted-foreground mt-1">{rec.description}</p>
                              </div>
                              <div className="flex gap-2">
                                <Badge variant="secondary">{rec.priority}</Badge>
                                <Badge variant="outline">{rec.effort_required}</Badge>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Quick Wins Tab */}
        <TabsContent value="quick-wins">
          <div className="space-y-4">
            {quickWins.map((win, index) => (
              <Card 
                key={index}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleQuickWinClick(win)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold">{win.title}</h3>
                      {win.issue && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Issue: {win.issue}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Badge className={getImpactBadgeColor(win.impact)}>
                        {win.impact} impact
                      </Badge>
                      <Badge className={getEffortBadgeColor(win.effort)}>
                        {win.effort} effort
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {quickWins.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground">
                    No quick wins identified yet. Run a comprehensive analysis to discover opportunities.
                  </p>
                  <Button 
                    className="mt-4"
                    onClick={() => navigate('/growth/analyze')}
                  >
                    Start Analysis
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Friction Alerts Tab */}
        <TabsContent value="alerts">
          <div className="space-y-4">
            {frictionAlerts.map((alert) => (
              <Card 
                key={alert.id}
                className={`border ${getSeverityColor(alert.severity)}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                          {alert.severity}
                        </Badge>
                        <span className="font-semibold">
                          {alert.friction_type.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        Affecting: {alert.affected_entity_name}
                      </p>
                      <p className="text-sm mb-2">
                        {alert.recommended_action}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{alert.event_count} occurrences</span>
                        <span>Since {new Date(alert.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => acknowledgeAlert(alert.id)}
                    >
                      Acknowledge
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {frictionAlerts.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    No active friction alerts. System is monitoring for issues.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Deep Analysis Tab */}
        <TabsContent value="analysis">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Competitor Analysis</CardTitle>
                <CardDescription>
                  See how you stack up against competitors
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate('/growth/competitors')}
                >
                  <Search className="mr-2 h-4 w-4" />
                  Analyze Competitors
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Content Gaps</CardTitle>
                <CardDescription>
                  Discover missing content opportunities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate('/growth/content-gaps')}
                >
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Find Content Gaps
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer Journey</CardTitle>
                <CardDescription>
                  Optimize paths to conversion
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate('/growth/journey')}
                >
                  <Users className="mr-2 h-4 w-4" />
                  Analyze Journeys
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
                <CardDescription>
                  Track growth and engagement metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate('/growth/metrics')}
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  View Metrics
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}